import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ColDef, GridOptions } from 'ag-grid-community';
import { ConfirmationDialogComponent } from 'src/app/Core/components/confirmation-dialog/confirmation-dialog.component';
import { ForceUpgradeService } from 'src/app/Core/services/force-upgrade.service';
import { ToasterService } from 'src/app/Core/services/toaster.service';
import { semverValidator } from 'src/app/Core/validators/semver.validator';

@Component({
  selector: 'app-force-upgrade',
  templateUrl: './force-upgrade.component.html',
  styleUrls: ['./force-upgrade.component.css'],
  providers: [DatePipe]
})
export class ForceUpgradeComponent {
  releaseForm: FormGroup = new FormGroup({});
  gridOptions: GridOptions;
  columnDefs: ColDef[];
  rowData: any[];
  dateFormat: string = 'dd MMM yyyy hh:mm:ss a';
  buttonTitle: string = 'Create Release Request';
  gridApi!: any;
  formData: any;
  platformMap: any = {
    android: 'Android',
    ios: 'iOS'
  };
  releaseTypeMap: any = {
    normal: 'Normal',
    force: 'Critical'
  };
  selectedPlatformFilter: 'android' | 'ios' = 'android';
  selectedRow: any
  selectedRowCount = 0;
  actionControl = this.formBuilder.control({ value: null, disabled: true });




  constructor(private formBuilder: FormBuilder, private datePipe: DatePipe, private api: ForceUpgradeService, private toaster: ToasterService, private dialog: MatDialog) {
    this.gridOptions = {
      defaultColDef: {
        flex: 1,
        resizable: true,
        wrapText: true,
        autoHeight: true,
        minWidth: 150,
      },
      rowSelection: 'single',
      suppressRowClickSelection: false
    };

    this.columnDefs = [
      {
        headerName: 'Actions',
        checkboxSelection: true,
        width: 60
      },
      { headerName: 'Id', field: 'id', wrapText: true, autoHeight: true },
      { headerName: 'Platform', field: 'platform', wrapText: true, autoHeight: true, filter: 'agTextColumnFilter', valueFormatter: (params) => this.platformMap[params.value] || params.value },
      {
        headerName: 'Version', field: 'version', wrapText: true, autoHeight: true, sortable: true, sort: 'desc', comparator: (a: string, b: string) => {
          const pa = a.split('.').map(Number);
          const pb = b.split('.').map(Number);

          for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
            const diff = (pa[i] || 0) - (pb[i] || 0);
            if (diff !== 0) {
              return diff;
            }
          }
          return 0;
        }
      },
      { headerName: 'Release Type', field: 'upgrade_type', wrapText: true, autoHeight: true, minWidth: 150, valueFormatter: (params) => this.releaseTypeMap[params.value] || params.value },
      { headerName: 'Release Notes English', field: 'upgrade_msg.en', wrapText: true, autoHeight: true, minWidth: 190 },
      { headerName: 'Release Notes Canada', field: 'upgrade_msg.fr', wrapText: true, autoHeight: true, minWidth: 190 },
      {
        headerName: `Created At (${this.timezoneOffset})`,
        field: 'created_at',
        valueFormatter: (params) =>
          this.formatDate(params.value),
        wrapText: true, autoHeight: true, minWidth: 210
      },
      {
        headerName: `Updated At (${this.timezoneOffset})`,
        field: 'updated_at',
        valueFormatter: (params) =>
          this.formatDate(params.value),
        wrapText: true, autoHeight: true, minWidth: 210
      },
      { headerName: 'Created By', field: 'created_by', wrapText: true, autoHeight: true },
      { headerName: 'Updated By', field: 'updated_by', wrapText: true, autoHeight: true },
    ];
  }

  ngOnInit(): void {
    this.releaseForm = this.formBuilder.group({
      selectedPlatformType: ['all', Validators.required],
      selecteReleaseType: ['all', Validators.required],
      versionNo: ['', [Validators.required, semverValidator()]],
      releaseNotesEnglish: [''],
      releaseNotesCanada: [''],
    });

    this.fetchReleases('android');
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  onSelectionChanged() {
    const selectedRows = this.gridApi.getSelectedRows();
    this.selectedRowCount = selectedRows.length;

    if (this.selectedRowCount === 0) {
      this.selectedRow = null;
      this.actionControl.reset();
      this.actionControl.disable();
      this.onClear()
    } else {
      this.selectedRow = selectedRows[0];
      this.actionControl.enable();
    }
  }

  onActionChange(action: 'update' | 'delete') {
    if (action === 'update') {
      this.updateRow(this.selectedRow);
    } else if (action === 'delete') {
      this.deleteRow(this.selectedRow);
    }
  }

  fetchReleases(platform: string) {
    this.api.getAllAppReleases(platform).subscribe({
      next: (data: any) => {
        this.onClear();
        this.selectedPlatformFilter = platform as 'android' | 'ios';
        this.rowData = data;
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          this.toaster.openDialogBox(error.error.message, false);
        }
      }
    });
  }

  deleteRow(row: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { confirmationText: 'Are you sure you want to delete this App release?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'yes') {
        this.api.deleteAppRelease(row.id, row.platform).subscribe({
          next: (data: any) => {
            this.toaster.openSnackBar(data.message, 'ok')
            this.onClear();
            this.fetchReleases(row.platform);
            this.actionControl.reset();
            this.actionControl.disable();
          },
          error: (error) => {
            if (error instanceof HttpErrorResponse) {
              this.toaster.openDialogBox(error.error.message, false);
            }
          }
        }
        );
      } else {
        this.actionControl.reset();
      }
    });
  }

  updateRow(row: any) {
    console.log("Update clicked", row);
    this.formData = {
      releaseId: row?.id ?? "",
      selectedPlatformType: row?.platform ?? "",
      versionNo: row?.version ?? "",
      selecteReleaseType: row?.upgrade_type ?? "",
      releaseNotesEnglish: row?.upgrade_msg.en ?? "",
      releaseNotesCanada: row?.upgrade_msg.fr ?? ""
    }
    this.releaseForm.patchValue(this.formData);

    this.releaseForm.get('selectedPlatformType')?.disable();
    this.releaseForm.get('versionNo')?.disable();
    this.buttonTitle = "Update Release"
  }

  onForceUpgradeSubmit(formDirective: FormGroupDirective) {
    if (this.buttonTitle !== "Create Release Request") {
      // Update Request handled here
      this.handleUpdate(formDirective);
      return;
    }

    // Create Request handled here
    const formValue = this.releaseForm.getRawValue();
    const selectedPlatform = formValue.selectedPlatformType;
    console.log("formValue:", formValue);
    console.log("selectedPlatform:", selectedPlatform);
    const platforms =
      selectedPlatform === 'both'
        ? ['android', 'ios']
        : [selectedPlatform];
    console.log("platforms:", platforms);
    this.handleCreateRelease(platforms, formDirective);
  }

  handleCreateRelease(platforms: string[], formDirective: FormGroupDirective) {
    const totalCalls = platforms.length;

    const callApiSequentially = (index: number) => {
      if (index >= totalCalls) {
        return;
      }

      const platform = platforms[index];

      const requestData = {
        platform,
        version: this.releaseForm.value.versionNo,
        upgrade_type: this.releaseForm.value.selecteReleaseType,
        upgrade_msg: {
          en: this.releaseForm.value.releaseNotesEnglish,
          fr: this.releaseForm.value.releaseNotesCanada
        }
      };

      console.log("requestData:", requestData);

      this.api.createAppRelease(requestData).subscribe({
        next: () => {
          // If last API call is successful
          if (index === totalCalls - 1) {
            this.toaster.openSnackBar("App release created successfully.", 'ok');
            this.resetForm(formDirective);
            totalCalls === 2 ? this.fetchReleases('android') : this.fetchReleases(platform);
          } else {
            // Move to next API call
            callApiSequentially(index + 1);
          }
        },
        error: (error) => {
          console.log("error=>", error);
          if (error instanceof HttpErrorResponse) {
            const errorMessage =
              totalCalls > 1 && index === 1
                ? 'App release for iOS failed'
                : error.error.message;
            if (error.error.message === 'resource already exists') {
              this.toaster.openDialogBox('This app release already exists.', false);
              return;
            }

            this.toaster.openDialogBox(errorMessage, false);
          }
        }
      });
    };

    // Start first API call
    callApiSequentially(0);
  }

  handleUpdate(formDirective: FormGroupDirective) {
    let requestData = {
      "upgrade_type": this.releaseForm.value.selecteReleaseType,
      "upgrade_msg": {
        "en": this.releaseForm.value.releaseNotesEnglish,
        "fr": this.releaseForm.value.releaseNotesCanada
      }
    }
    let platform = this.releaseForm.getRawValue().selectedPlatformType;
    let releaseId = this.formData.releaseId;

    this.api.updateAppRelease(requestData, platform, releaseId).subscribe(
      {
        next: () => {
          this.toaster.openSnackBar("App release updated successfully.", 'ok')
          this.resetForm(formDirective);
          this.formData = {};
          this.fetchReleases(platform);
          this.actionControl.reset();
          this.actionControl.disable();
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            this.toaster.openDialogBox(error.error.message, false);
          }
        }
      }
    );
  }


  onPlatformFilterChange(platform: string) {
    console.log("platform:", platform);
    this.actionControl.reset();
    this.actionControl.disable();
    this.fetchReleases(platform);
  }

  onClear() {
    this.releaseForm.reset();
    this.releaseForm.get('selectedPlatformType')?.enable();
    this.releaseForm.get('versionNo')?.enable();
    this.buttonTitle = "Create Release Request"
    this.actionControl.reset();
  }

  resetForm(formDirective: FormGroupDirective) {
    formDirective.resetForm();
    this.releaseForm.reset();

    this.releaseForm.get('selectedPlatformType')?.enable();
    this.releaseForm.get('versionNo')?.enable();

    this.buttonTitle = 'Create Release Request';
  }

  formatDate(value: string): string {
    if (!value) return '';
    return this.datePipe.transform(value, this.dateFormat) || '';
  }

  get timezoneOffset(): string {
    const offset = new Date().getTimezoneOffset(); // in minutes
    const absOffset = Math.abs(offset);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const minutes = String(absOffset % 60).padStart(2, '0');
    const sign = offset <= 0 ? '+' : '-';
    return `GMT${sign}${hours}:${minutes}`;
  }
}
